import { useCallback } from 'react';

import type { IConversation } from 'src/api/landlord-messages-api';
import type { ILead } from 'src/services/api/leads/types';
import type { IUser } from 'src/api/users-api';
import { isUserAway } from 'src/api/users-api';

interface IOptions {
    teamMembers?: IUser[];
    currentUserId?: number;
    enabled: boolean;
}

/**
 * identifies unread conversations owned by an offline
 * (away) teammate other than the current user.
 *
 * :returns: A stable ``(conversation, lead) => boolean`` predicate.
 */
export const useUnreadFromOfflineTeammatePredicate = ({
    teamMembers,
    currentUserId,
    enabled,
}: IOptions): ((conversation: IConversation, lead?: ILead) => boolean) => {
    return useCallback((conversation: IConversation, lead?: ILead) => {
        if (!enabled || !teamMembers) {
            return false;
        }
        if (conversation.num_unread_messages <= 0) {
            return false;
        }
        const owner = lead?.owner;
        if (owner == null || owner === currentUserId) {
            return false;
        }
        return isUserAway(teamMembers.find(m => m.id === owner));
    }, [enabled, teamMembers, currentUserId]);
};
